import * as React from "react";
import { PropsWithChildren } from "react";
import {
    Box,
    Grid,
    GridProps,
    Paper,
    Stack,
    styled,
    Tab,
    Tabs,
    Theme,
    Typography,
} from "@mui/material";
import steeringImage from "assets/img/brian-scott-I3x48zJoCSg-unsplash.jpg";
import cancerImage from "assets/img/national-cancer-institute-59pGROkKJPE-unsplash.jpg";
import galaxyImage from "assets/img/green-pea-galaxy-wikipedia.png";
import LinkCard from "components/LinkCard";
import { SxProps } from "@mui/system";
import { PageContainer } from "#pages/PageContainer";
import { push } from "redux-first-history";
import { Routes } from "#/config";
import { ControlledCommandLine } from "../commmandLine/ControlledCommandLine";
import { colorStringToRgbString } from "util/colors";
import { generatePath } from "react-router";
import { generateUrl } from "#components/App/routing/generateUrl";
import { useAppDispatch, useAppSelector } from "#store";
import { NlExamplesChooser } from "#pages/landing/NlExamplesChooser";
import { setDatasource, setQuery } from "#store/ui";
import { H1, P } from "#common/headings";
import { DatasourceEnum } from "#/services/pilotService/types";
import { ActionType } from "#services/pilotService/types";
import { SqlSearchComponent } from "./SqlSearchPage";
import { QlInputComponent } from "#common/QlInputComponent";

const splashStyle: SxProps<Theme> = {
    background: theme =>
        `linear-gradient(135deg, ${colorStringToRgbString(
            theme.palette.primary.main
        )} 0%, ${colorStringToRgbString(theme.palette.success.main)} 100%)`, // background: "linear-gradient(135deg, rgba(109,194,12,1) 0%, rgba(20,73,140,1) 33%, rgba(20,73,140,1) 66%, rgba(106,15,142,1) 100%)",
    // backgroundImage: (theme) => `url(${theme.palette.mode === "dark" ? darkBgImage : lightBgImage })`,
    // backgroundSize: "cover"
};
const giganticStyle: SxProps<Theme> = {
    fontSize: "6rem",
};

const stripe: SxProps<Theme> = {
    minHeight: "50vh", // width: "100vw"
    // backdropFilter: "blur(4px)"
    // background: (theme) => theme.palette.background.default
};

const odd: SxProps<Theme> = {
    // background: theme => alpha(theme.palette.background.default, 0.5),
};
const even: SxProps<Theme> = {
    // background: theme => alpha(lighten(theme.palette.background.default, 0.5), 0.9)
};
const wide: SxProps<Theme> = {
    // maxWidth: "100%",
};
const narrow: SxProps<Theme> = {
    // maxWidth: "xs",
};

export const Gradient = styled("div")(({ theme }) => ({
    background: `linear-gradient(135deg, ${colorStringToRgbString(
        theme.palette.primary.main
    )} 0%, ${colorStringToRgbString(theme.palette.secondary.main)} 100%)`,
    color: theme.palette.primary.contrastText,
}));

const NormalGrid = ({ children, ...other }: PropsWithChildren<GridProps>) => (
    <Grid
        container
        alignSelf={"center"}
        direction="column"
        alignContent="center"
        alignItems="center"
        justifyContent="center"
        maxWidth="lg"
        {...other}
    >
        {children}
    </Grid>
);

const WideGrid = ({ children, ...other }: PropsWithChildren<GridProps>) => (
    <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        {...other}
    >
        {children}
    </Grid>
);

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

enum InputModality {
    NL,
    QRECS,
    QL,
}

export const Landing = () => {
    const dispatch = useAppDispatch();
    const currentQuery = useAppSelector(state => state.uiState.query);
    const datasourceDto = useAppSelector(
        state => state.uiState.selectedDatasource
    );
    const currentDatasource = datasourceDto.name;
    const currentOp = ActionType.NL2SQL;
    const [inputModality, setInputModality] = React.useState<InputModality>(
        InputModality.NL
    );

    const handleChangeInputModality = (
        event: React.SyntheticEvent,
        newValue: InputModality
    ) => {
        setInputModality(newValue);
    };

    const [value, setValue] = React.useState(
        // "Find projects where Alberto Broggi was involved."
        currentQuery || "Find the topics of projects that ended in 2014."
    );
    React.useEffect(() => {
        dispatch(setQuery(value));
    }, [value, dispatch]);

    const onSubmit = (
        q: string = value,
        datasource: DatasourceEnum = currentDatasource,
        op: ActionType = currentOp
    ) => {
        dispatch(
            push(
                generateUrl(generatePath(Routes.triggerAction.path), {
                    q: q,
                    op: op,
                    o: datasourceDto.queryLanguage,
                    db: datasource,
                })
            )
        );
    };

    return (
        <Gradient>
            <PageContainer maxWidth={false}>
                <Stack alignSelf={"center"}>
                    <NormalGrid sx={{ ...odd }}>
                        <Grid
                            item
                            container
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid item xs={12}>
                                <Typography
                                    variant={"h1"}
                                    sx={{ ...giganticStyle }}
                                >
                                    INODE
                                </Typography>
                                <Typography
                                    variant={"h2"}
                                    sx={{ fontSize: "4rem" }}
                                >
                                    Intelligent data exploration
                                </Typography>
                                <P>
                                    INODE aims at simplifying access to data, by
                                    allowing a more dialectic and intuitive
                                    interaction with data.
                                </P>
                            </Grid>
                        </Grid>
                    </NormalGrid>
                    <NormalGrid sx={{ ...even, ...stripe }}>
                        <Grid
                            item
                            container
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid item xs={12}>
                                <Paper
                                    sx={{
                                        padding: "1rem",
                                        marginTop: "2rem",
                                        marginBottom: "10rem",
                                    }}
                                    elevation={10}
                                >
                                    <Box
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Tabs
                                            value={inputModality}
                                            onChange={handleChangeInputModality}
                                            aria-label="Choose input modality"
                                        >
                                            <Tab
                                                label="Search by Natural language"
                                                {...a11yProps(InputModality.NL)}
                                            />
                                            <Tab
                                                label="Search by Query Builder"
                                                {...a11yProps(
                                                    InputModality.QRECS
                                                )}
                                            />
                                            <Tab
                                                label="Search by Structured Query Input"
                                                {...a11yProps(InputModality.QL)}
                                            />
                                        </Tabs>
                                    </Box>
                                    <TabPanel
                                        value={inputModality}
                                        index={InputModality.NL}
                                    >
                                        {/*<H1 sx={{ paddingTop: "6rem" }}>*/}
                                        {/*    Natural language search:*/}
                                        {/*</H1>*/}
                                        <ControlledCommandLine
                                            value={value}
                                            onChange={value => {
                                                setValue(value);
                                            }}
                                            error={false}
                                            onSubmit={v =>
                                                onSubmit(
                                                    value,
                                                    currentDatasource,
                                                    currentOp
                                                )
                                            }
                                        />
                                    </TabPanel>
                                    <TabPanel
                                        value={inputModality}
                                        index={InputModality.QL}
                                    >
                                        <QlInputComponent />
                                    </TabPanel>
                                    <TabPanel
                                        value={inputModality}
                                        index={InputModality.QRECS}
                                    >
                                        {/*<Grid*/}
                                        {/*    item*/}
                                        {/*    container*/}
                                        {/*    justifyContent="center"*/}
                                        {/*    alignItems="center"*/}
                                        {/*>*/}
                                        {/*    <Grid item xs={12} mt={5}>*/}
                                        {/*        <H1> Query Builder</H1>*/}
                                        {/*<Paper*/}
                                        {/*    sx={{*/}
                                        {/*        padding: "1rem",*/}
                                        {/*        marginTop: "2rem",*/}
                                        {/*        marginBottom: "10rem",*/}
                                        {/*    }}*/}
                                        {/*    elevation={10}*/}
                                        {/*>*/}
                                        <SqlSearchComponent />
                                        {/*</Paper>*/}
                                        {/*    </Grid>*/}
                                        {/*</Grid>*/}
                                        {/* <Grid item xs={12} container spacing={4}>
                            <Grid item xs={4}>
                                <LinkCard
                                    action={() =>
                                        alert(
                                            "Fill the sql editor with content of this card"
                                        )
                                    }
                                    img={{
                                        alt: "A desk by David Rangel on Unsplash",
                                        href: codeImage,
                                    }}
                                    title="Cancer Research"
                                    text={
                                        <>
                                            <SqlHighlighter
                                                code={
                                                    "SELECT * FROM '...' WHERE '...'"
                                                }
                                            />{" "}
                                            is a typical example for an sql
                                            query.
                                        </>
                                    }
                                    actionText={
                                        "Fill the SQL editor with this example"
                                    }
                                />
                            </Grid>
                            <Grid item md={4}>
                                <LinkCard
                                    action={() =>
                                        alert(
                                            "Fill the sql editor with content of this card"
                                        )
                                    }
                                    img={{
                                        alt: "A desk by David Rangel on Unsplash",
                                        href: codeImage,
                                    }}
                                    title="Astronomy"
                                    text={
                                        <>
                                            <SqlHighlighter
                                                code={
                                                    "SELECT * FROM '...' WHERE '...'"
                                                }
                                            />{" "}
                                            is a typical example for an sql
                                            query.
                                        </>
                                    }
                                    actionText={
                                        "Fill the SQL editor with this example"
                                    }
                                />{" "}
                            </Grid>
                            <Grid item md={4}>
                                <LinkCard
                                    action={() =>
                                        alert(
                                            "Fill the sql editor with content of this card"
                                        )
                                    }
                                    img={{
                                        alt: "A desk by David Rangel on Unsplash",
                                        href: codeImage,
                                    }}
                                    title="Policy making"
                                    text={
                                        <>
                                            <SqlHighlighter
                                                code={
                                                    "SELECT * FROM '...' WHERE '...'"
                                                }
                                            />{" "}
                                            is a typical example for an sql
                                            query.
                                        </>
                                    }
                                    actionText={
                                        "Fill the table editor with this example"
                                    }
                                />
                            </Grid>
                        </Grid> */}
                                    </TabPanel>
                                </Paper>
                            </Grid>
                        </Grid>
                    </NormalGrid>
                    <NormalGrid sx={{ ...even }}>
                        {/*<Grid item xs={12}>
                            <H1>Find data with OpenDataDialog:</H1>
                            <H2>Easy as 1, 2, 3</H2>
                            <Grid container spacing={4}>
                                <Grid item md={4}>
                                    <Card>
                                        <H1>1.</H1>
                                        <P>Type in what you are looking for.</P>
                                        <Caption>
                                            Once you hit{" "}
                                            <a onClick={() => onSubmit()}>
                                                search
                                            </a>
                                            , OpenDataDialog searches for data
                                            sets that may satisfy your need for
                                            information.
                                        </Caption>
                                    </Card>
                                </Grid>
                                <Grid item md={4}>
                                    <Card>
                                        <H1>2.</H1>
                                        <P>
                                            Add most promising results to your{" "}
                                            {Routes.dataLibrary.label}.{" "}
                                            <FavoriteAddIcon />
                                        </P>
                                        <Caption>
                                            Just like browsing in the library,
                                            you pick a few books that look
                                            promising, then take a seat to check
                                            if one of those actually satisfies
                                            your information need. Once you have
                                            found a few promising results, you
                                            can investigate them further in the{" "}
                                            <Link
                                                to={generatePath(
                                                    Routes.dataLibrary.path
                                                )}
                                            >
                                                {Routes.dataLibrary.label}
                                            </Link>
                                        </Caption>
                                    </Card>
                                </Grid>
                                <Grid item md={4}>
                                    <Card>
                                        <H1>3.</H1>
                                        <P>
                                            Compare most promising results,
                                            check results in detail. Decide.{" "}
                                            <Psychology />
                                        </P>
                                        <Caption>
                                            Similar to shopping, its now the
                                            time choose between different items
                                            in your cart. Because in the end,
                                            you only want to have one result,
                                            and that should be the best one.
                                        </Caption>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Grid>
                    </NormalGrid>
                    <NormalGrid sx={{ ...odd }}>*/}
                        <Grid item xs={12}>
                            <H1>Multiple use cases</H1>
                            <Grid
                                container
                                spacing={4}
                                sx={{ marginTop: "1rem" }}
                            >
                                <Grid item md={4}>
                                    <LinkCard
                                        action={() =>
                                            dispatch(
                                                setDatasource(
                                                    DatasourceEnum.cordis
                                                )
                                            )
                                        }
                                        img={{
                                            alt: "Steering image by Brian Scott on UnSplash",
                                            href: steeringImage,
                                        }}
                                        title="EU Research Project Data"
                                        text={
                                            "Research & Innovation data is fragmented, highly heterogeneous and disconnected. They are often neither in structured format nor systematically shared across organisations. To leverage the full potential of R&I open data platforms, ontology-based solutions are needed to help bring together inputs from a variety of sources in an interoperable fashion and enable powerful queries even non-technical users, who are the majority in R&I use cases. "
                                        }
                                        actionText={"Explore CORDIS data"}
                                    />
                                </Grid>
                                <Grid item md={4}>
                                    <LinkCard
                                        action={() =>
                                            dispatch(
                                                setDatasource(
                                                    DatasourceEnum.sdss
                                                )
                                            )
                                        }
                                        img={{
                                            alt: "An image showing a galaxy",
                                            href: galaxyImage,
                                        }}
                                        title="Astonomical observations data"
                                        text={
                                            "Since the Sloan Digital Survey (SDSS), which collected spectra of thousands of galaxies throughout vast distances in space, we have been amassing large databases of astronomical data. It is now a challenge to store, transport and query this data, because of volume and heterogeneity issues. Instead of designing several thousand-character-long nested SQL statements and developing routines to match them against imaging or spectroscopic data, INODE can enable retrieving information by means of natural language querying."
                                        }
                                        actionText={"Explore SDSS data"}
                                    />
                                </Grid>
                                <Grid item md={4}>
                                    <LinkCard
                                        action={() =>
                                            dispatch(
                                                setDatasource(
                                                    DatasourceEnum.oncomx
                                                )
                                            )
                                        }
                                        img={{
                                            alt: "A X-ray photograph of a human torso",
                                            href: cancerImage,
                                        }}
                                        title="Cancer research data"
                                        text={`Cancer genomics studies generate
                            highly heterogeneous data sets with
                            variable characteristics. These data
                            sets have different file formats,
                            attribute names, positional
                            coordinates, reference data,
                            processing pipelines, and more: to
                            accelerate extraction of useful
                            knowledge, sophisticated semantic
                            queries should be enabled across
                            large data sets via an intuitive
                            interface.`}
                                        actionText={"Explore OncoMX data"}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </NormalGrid>
                    <WideGrid sx={{ ...odd, marginTop: "2rem" }}>
                        <Grid item xs={12}>
                            <NlExamplesChooser onSubmit={onSubmit} />
                        </Grid>
                    </WideGrid>

                    {/*<NormalGrid>*/}
                    {/*    <Grid item xs={12}>*/}
                    {/*        <Typography variant="h1">*/}
                    {/*            Intelligent data exploration*/}
                    {/*        </Typography>*/}
                    {/*        <Typography>*/}
                    {/*            INODE – Intelligent Open Data Exploration aims*/}
                    {/*            at*/}
                    {/*            simplifying access to data, by allowing a more*/}
                    {/*            dialectic and intuitive interaction with data,*/}
                    {/*            similar to a dialog with a human.*/}
                    {/*        </Typography>*/}
                    {/*        <Typography>*/}
                    {/*            The goal of INODE is to offer a suite of agile,*/}
                    {/*            fit-for-purpose and sustainable services for*/}
                    {/*            exploration of open data sets:*/}
                    {/*        </Typography>*/}
                    {/*        <ol>*/}
                    {/*            <li>*/}
                    {/*                search data using natural language,*/}
                    {/*                examples,*/}
                    {/*                and analytics*/}
                    {/*            </li>*/}
                    {/*            <li>*/}
                    {/*                get guidance from the system in*/}
                    {/*                understanding*/}
                    {/*                the data and formulating the right queries,*/}
                    {/*                and*/}
                    {/*            </li>*/}
                    {/*            <li>*/}
                    {/*                visually explore and optimize answers and*/}
                    {/*                queries and get insights through interactive*/}
                    {/*                visualizations.*/}
                    {/*            </li>*/}
                    {/*        </ol>*/}
                    {/*    </Grid>*/}
                    {/*</NormalGrid>*/}
                </Stack>
            </PageContainer>
        </Gradient>
    );
};
